import { motion } from 'framer-motion'
import { 
  ArrowLeft, 
  LogOut,
  Stethoscope,
  TrendingUp,
  AlertTriangle,
  CheckCircle,
  Clock,
  Users,
  BarChart3,
  Heart,
  Calendar,
  Pill,
  Shield,
  Activity,
  Brain,
  Target,
  Eye,
  FileText,
  Phone
} from 'lucide-react'
import InfoTooltip from '../common/InfoTooltip'

export default function ClinicaDashboard({ onBack, onLogout, businessData }) {
  const kpis = [
    {
      title: 'Precisión Diagnóstica',
      value: '97%',
      change: '+5%',
      trend: 'up',
      icon: Target,
      color: 'green',
      tooltip: {
        title: 'Precisión en Diagnósticos Médicos',
        description: 'Porcentaje de precisión en diagnósticos asistidos por IA, mejorando la exactitud y reduciendo errores médicos.',
        aiMethod: 'Análisis de síntomas con ML, diagnóstico diferencial automático, validación cruzada con bases médicas.',
        dataSource: 'Historial médico de pacientes, bases de datos de síntomas, literatura médica, resultados de laboratorio.',
        updateFrequency: 'Validación continua con cada diagnóstico'
      }
    },
    {
      title: 'Reducción Tiempo',
      value: '35%',
      change: '+12%',
      trend: 'up',
      icon: Clock,
      color: 'blue',
      description: 'Tiempo consulta',
      tooltip: {
        title: 'Optimización de Tiempo de Consulta',
        description: 'Reducción en el tiempo promedio de consulta mediante automatización de procesos y asistencia inteligente.',
        aiMethod: 'Automatización de documentación, sugerencias de diagnóstico, optimización de flujo de consulta.',
        dataSource: 'Tiempos históricos de consulta, eficiencia por médico, complejidad de casos.',
        updateFrequency: 'Medición en tiempo real con análisis semanal'
      }
    },
    {
      title: 'Satisfacción Paciente',
      value: '91%',
      change: '+7%',
      trend: 'up',
      icon: Heart,
      color: 'red',
      tooltip: {
        title: 'Índice de Satisfacción de Pacientes',
        description: 'Medición de la satisfacción del paciente mejorada mediante personalización de atención y seguimiento inteligente.',
        aiMethod: 'Análisis de feedback de pacientes, predicción de satisfacción, personalización de comunicación.',
        dataSource: 'Encuestas post-consulta, tiempos de espera, calidad de atención percibida.',
        updateFrequency: 'Evaluación continua tras cada consulta'
      }
    },
    {
      title: 'Detección Temprana',
      value: '84%',
      change: '+22%',
      trend: 'up',
      icon: Eye,
      color: 'purple',
      description: 'Casos detectados',
      tooltip: {
        title: 'Detección Temprana de Enfermedades',
        description: 'Porcentaje de casos donde la IA detecta signos tempranos de enfermedades antes de manifestación clínica completa.',
        aiMethod: 'Análisis predictivo de patrones de salud, detección de anomalías en signos vitales, screening inteligente.',
        dataSource: 'Histórico médico, signos vitales, resultados de laboratorio, factores de riesgo.',
        updateFrequency: 'Análisis continuo con alertas automáticas'
      }
    }
  ]

  const predictiveAnalysis = [
    {
      condition: 'Diabetes Tipo 2',
      riskPatients: 23,
      accuracy: 94,
      earlyDetection: 87,
      recommendation: 'Programa preventivo para 23 pacientes de alto riesgo',
      color: 'red'
    },
    {
      condition: 'Hipertensión',
      riskPatients: 34,
      accuracy: 91,
      earlyDetection: 89,
      recommendation: 'Monitoreo continuo y ajuste de tratamiento',
      color: 'orange'
    },
    {
      condition: 'Enfermedades Cardíacas',
      riskPatients: 12,
      accuracy: 96,
      earlyDetection: 92,
      recommendation: 'Derivación inmediata a cardiología especializada',
      color: 'red'
    },
    {
      condition: 'Depresión',
      riskPatients: 18,
      accuracy: 88,
      earlyDetection: 76,
      recommendation: 'Evaluación psicológica y seguimiento cercano',
      color: 'blue'
    }
  ]

  const patientInsights = [
    {
      name: 'María Rodríguez',
      age: 45,
      riskLevel: 'Alto',
      condition: 'Pre-diabetes',
      nextVisit: '15 Jun',
      aiRecommendation: 'Dieta baja en carbohidratos + ejercicio',
      compliance: 78
    },
    {
      name: 'Juan Pérez',
      age: 62,
      riskLevel: 'Medio',
      condition: 'Hipertensión leve',
      nextVisit: '18 Jun',
      aiRecommendation: 'Ajustar medicación, reducir sodio',
      compliance: 92
    },
    {
      name: 'Ana García',
      age: 38,
      riskLevel: 'Bajo',
      condition: 'Chequeo preventivo',
      nextVisit: '22 Jun',
      aiRecommendation: 'Mantener rutina actual de ejercicio',
      compliance: 96
    }
  ]

  const treatmentEffectiveness = [
    { treatment: 'Metformina - Diabetes', patients: 45, effectiveness: 94, sideEffects: 8 },
    { treatment: 'Losartán - Hipertensión', patients: 67, effectiveness: 89, sideEffects: 12 },
    { treatment: 'Atorvastatina - Colesterol', patients: 34, effectiveness: 92, sideEffects: 6 },
    { treatment: 'Omeprazol - Gastritis', patients: 28, effectiveness: 87, sideEffects: 4 },
    { treatment: 'Levotiroxina - Tiroides', patients: 23, effectiveness: 96, sideEffects: 3 }
  ]

  const appointmentOptimization = [
    { hour: '8:00', scheduled: 6, actualTime: '45 min', efficiency: 85, waitTime: '5 min' },
    { hour: '9:00', scheduled: 8, actualTime: '52 min', efficiency: 78, waitTime: '12 min' },
    { hour: '10:00', scheduled: 7, actualTime: '48 min', efficiency: 83, waitTime: '8 min' },
    { hour: '11:00', scheduled: 6, actualTime: '44 min', efficiency: 88, waitTime: '6 min' },
    { hour: '16:00', scheduled: 8, actualTime: '55 min', efficiency: 75, waitTime: '15 min' },
    { hour: '17:00', scheduled: 5, actualTime: '41 min', efficiency: 92, waitTime: '3 min' }
  ]

  const medicalAlerts = [
    {
      type: 'critical',
      patient: 'Carlos Méndez',
      condition: 'Presión arterial 180/110',
      time: 'Hace 1h',
      action: 'Atención inmediata requerida'
    },
    {
      type: 'warning',
      patient: 'Lucía Fernández',
      condition: 'Glucosa elevada - 280 mg/dl',
      time: 'Hace 2h',
      action: 'Ajustar medicación diabetes'
    },
    {
      type: 'info',
      patient: 'Roberto Silva',
      condition: 'Recordatorio mamografía',
      time: 'Hace 3h',
      action: 'Agendar cita de seguimiento'
    }
  ]

  const aiInsights = [
    {
      type: 'prediction',
      message: 'Patrón de síntomas sugiere brote de gripe estacional en 2 semanas',
      confidence: 91,
      action: 'Preparar vacunación preventiva'
    },
    {
      type: 'optimization',
      message: 'Reagrupar citas de diabéticos los miércoles mejorará seguimiento',
      confidence: 87,
      action: 'Reorganizar agenda'
    },
    {
      type: 'alert',
      message: '15% de pacientes muestran baja adherencia a tratamiento hipertensión',
      confidence: 95,
      action: 'Programa de educación necesario'
    }
  ]

  const getColorClasses = (color) => {
    const colors = {
      blue: 'bg-blue-100 text-blue-800',
      green: 'bg-green-100 text-green-800',
      purple: 'bg-purple-100 text-purple-800',
      orange: 'bg-orange-100 text-orange-800',
      red: 'bg-red-100 text-red-800'
    }
    return colors[color] || colors.blue
  }

  const getIconColorClasses = (color) => {
    const colors = {
      blue: 'bg-blue-500',
      green: 'bg-green-500',
      purple: 'bg-purple-500',
      orange: 'bg-orange-500',
      red: 'bg-red-500'
    }
    return colors[color] || colors.blue
  }

  const getRiskColor = (risk) => {
    const colors = {
      'Alto': 'bg-red-100 text-red-800 border-red-200',
      'Medio': 'bg-yellow-100 text-yellow-800 border-yellow-200',
      'Bajo': 'bg-green-100 text-green-800 border-green-200'
    }
    return colors[risk] || colors['Medio']
  }

  const getAlertColor = (type) => {
    const colors = {
      critical: 'bg-red-100',
      warning: 'bg-yellow-100',
      info: 'bg-blue-100'
    }
    return colors[type] || colors.info
  }

  const getAlertIcon = (type) => {
    if (type === 'critical') return AlertTriangle
    if (type === 'warning') return Clock
    return FileText
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <button
                onClick={onBack}
                className="flex items-center px-3 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors mr-4"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Volver
              </button>
              <div className="flex items-center">
                <div className={`w-8 h-8 bg-gradient-to-br ${businessData.color} rounded-lg flex items-center justify-center mr-3`}>
                  <Stethoscope className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h1 className="text-xl font-semibold text-gray-900">{businessData.title}</h1>
                  <p className="text-sm text-gray-500">HealthTech Analytics IA</p>
                </div>
              </div>
            </div>
            <button
              onClick={onLogout}
              className="flex items-center px-4 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Cerrar Sesión
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* KPIs Row */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8"
        >
          {kpis.map((kpi, index) => {
            const IconComponent = kpi.icon
            return (
              <div key={index} className="bg-white rounded-xl shadow-sm border p-6">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2">
                      <p className="text-sm text-gray-600">{kpi.title}</p>
                      <InfoTooltip 
                        title={kpi.tooltip.title}
                        description={kpi.tooltip.description}
                        aiMethod={kpi.tooltip.aiMethod}
                        dataSource={kpi.tooltip.dataSource}
                        updateFrequency={kpi.tooltip.updateFrequency}
                        iconColor="text-gray-400"
                      />
                    </div>
                    <p className="text-2xl font-bold text-gray-900">{kpi.value}</p>
                    <div className="flex items-center mt-2">
                      <span className={`text-sm font-medium ${kpi.trend === 'up' ? 'text-green-600' : 'text-red-600'}`}>
                        {kpi.change}
                      </span>
                      <span className="text-sm text-gray-500 ml-1">
                        {kpi.description || 'vs mes anterior'}
                      </span>
                    </div>
                  </div>
                  <div className={`p-3 ${getIconColorClasses(kpi.color)} rounded-lg`}>
                    <IconComponent className="w-6 h-6 text-white" />
                  </div>
                </div>
              </div>
            )
          })}
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column */}
          <div className="lg:col-span-2 space-y-8">
            {/* Análisis Predictivo */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-xl shadow-sm border p-6"
            >
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-2">
                  <h3 className="text-lg font-semibold text-gray-900">Análisis Predictivo de Riesgos</h3>
                  <InfoTooltip 
                    title="Sistema de Análisis Predictivo de Riesgos Médicos"
                    description="IA que analiza factores de riesgo para predecir probabilidades de desarrollar enfermedades específicas."
                    aiMethod="Machine learning sobre factores de riesgo, análisis de historiales médicos, modelos predictivos de salud."
                    dataSource="Historial médico familiar, estilo de vida, exámenes de laboratorio, factores genéticos."
                    updateFrequency="Análisis continuo con alertas automáticas de riesgo elevado"
                    iconColor="text-gray-400"
                  />
                </div>
                <div className="flex items-center space-x-2">
                  <Brain className="w-5 h-5 text-red-600" />
                  <span className="text-sm text-gray-600">HealthAI Engine</span>
                </div>
              </div>
              
              <div className="space-y-6">
                {predictiveAnalysis.map((analysis, index) => (
                  <div key={index} className="border border-gray-100 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-medium text-gray-900">{analysis.condition}</h4>
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${getColorClasses(analysis.color)}`}>
                        {analysis.riskPatients} pacientes en riesgo
                      </span>
                    </div>
                    
                    <div className="grid grid-cols-3 gap-4 mb-3">
                      <div>
                        <p className="text-sm text-gray-600">Precisión IA</p>
                        <div className="mt-1 bg-gray-200 rounded-full h-2">
                          <div 
                            className="h-2 rounded-full bg-blue-500"
                            style={{ width: `${analysis.accuracy}%` }}
                          />
                        </div>
                        <p className="text-sm font-medium mt-1">{analysis.accuracy}%</p>
                      </div>
                      
                      <div>
                        <p className="text-sm text-gray-600">Detección Temprana</p>
                        <div className="mt-1 bg-gray-200 rounded-full h-2">
                          <div 
                            className="h-2 rounded-full bg-green-500"
                            style={{ width: `${analysis.earlyDetection}%` }}
                          />
                        </div>
                        <p className="text-sm font-medium mt-1">{analysis.earlyDetection}%</p>
                      </div>
                      
                      <div>
                        <p className="text-sm text-gray-600">Pacientes Riesgo</p>
                        <p className="text-lg font-bold text-red-600">{analysis.riskPatients}</p>
                      </div>
                    </div>
                    
                    <div className="bg-red-50 rounded-lg p-3">
                      <p className="text-sm text-red-800">
                        <strong>Recomendación:</strong> {analysis.recommendation}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Efectividad de Tratamientos */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-xl shadow-sm border p-6"
            >
              <h3 className="text-lg font-semibold text-gray-900 mb-6">
                Efectividad de Tratamientos
              </h3>
              
              <div className="space-y-4">
                {treatmentEffectiveness.map((treatment, index) => (
                  <div key={index} className="flex items-center space-x-4 p-3 bg-gray-50 rounded-lg">
                    <div className="flex-1">
                      <h4 className="font-medium text-gray-900 text-sm">{treatment.treatment}</h4>
                      <p className="text-xs text-gray-600">{treatment.patients} pacientes</p>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4 text-center">
                      <div>
                        <p className="text-sm text-gray-600">Efectividad</p>
                        <p className={`text-lg font-bold ${treatment.effectiveness >= 90 ? 'text-green-600' : treatment.effectiveness >= 80 ? 'text-blue-600' : 'text-yellow-600'}`}>
                          {treatment.effectiveness}%
                        </p>
                      </div>
                      
                      <div>
                        <p className="text-sm text-gray-600">Efectos Sec.</p>
                        <p className={`text-lg font-bold ${treatment.sideEffects <= 5 ? 'text-green-600' : treatment.sideEffects <= 10 ? 'text-yellow-600' : 'text-red-600'}`}>
                          {treatment.sideEffects}%
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>

          {/* Right Column */}
          <div className="space-y-8">
            {/* Alertas Médicas */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-white rounded-xl shadow-sm border p-6"
            >
              <div className="flex items-center space-x-2 mb-6">
                <h3 className="text-lg font-semibold text-gray-900">Alertas Médicas</h3>
                <InfoTooltip 
                  title="Sistema de Alertas Médicas Inteligentes"
                  description="Alertas automáticas generadas por IA para situaciones médicas que requieren atención inmediata."
                  aiMethod="Análisis de signos vitales, detección de patrones anómalos, alertas basadas en protocolos médicos."
                  dataSource="Monitoreo de pacientes, signos vitales, historial médico, protocolos de emergencia."
                  updateFrequency="Monitoreo continuo con alertas instantáneas"
                  iconColor="text-gray-400"
                />
              </div>
              
              <div className="space-y-4">
                {medicalAlerts.map((alert, index) => {
                  const IconComponent = getAlertIcon(alert.type)
                  return (
                    <div key={index} className="border border-gray-100 rounded-lg p-3">
                      <div className="flex items-start space-x-3">
                        <div className={`p-2 rounded-full flex-shrink-0 ${getAlertColor(alert.type)}`}>
                          <IconComponent className="w-4 h-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="text-sm font-medium text-gray-900">{alert.patient}</h4>
                          <p className="text-sm text-gray-700 mt-1">{alert.condition}</p>
                          <div className="flex items-center justify-between mt-2">
                            <span className="text-xs text-gray-500">{alert.time}</span>
                            <button className="text-xs text-blue-600 hover:text-blue-800 font-medium">
                              {alert.action}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            </motion.div>

            {/* Perfiles de Pacientes */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="bg-white rounded-xl shadow-sm border p-6"
            >
              <div className="flex items-center space-x-2 mb-6">
                <h3 className="text-lg font-semibold text-gray-900">Pacientes de Alto Riesgo</h3>
                <InfoTooltip 
                  title="Identificación de Pacientes de Alto Riesgo"
                  description="Sistema que identifica automáticamente pacientes con mayor probabilidad de complicaciones o necesidad de atención especializada."
                  aiMethod="Scoring de riesgo basado en ML, análisis de factores múltiples, predicción de outcomes."
                  dataSource="Historial médico completo, resultados de laboratorio, factores de riesgo, tratamientos actuales."
                  updateFrequency="Evaluación continua con actualización tras cada consulta"
                  iconColor="text-gray-400"
                />
              </div>
              
              <div className="space-y-4">
                {patientInsights.map((patient, index) => (
                  <div key={index} className="border border-gray-100 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <h4 className="font-medium text-gray-900">{patient.name}</h4>
                        <p className="text-sm text-gray-600">{patient.age} años</p>
                      </div>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getRiskColor(patient.riskLevel)}`}>
                        {patient.riskLevel}
                      </span>
                    </div>
                    
                    <div className="space-y-2">
                      <div className="text-sm">
                        <span className="text-gray-600">Condición:</span>
                        <span className="ml-1 font-medium">{patient.condition}</span>
                      </div>
                      
                      <div className="text-sm">
                        <span className="text-gray-600">Próxima visita:</span>
                        <span className="ml-1 font-medium">{patient.nextVisit}</span>
                      </div>
                      
                      <div className="text-sm">
                        <span className="text-gray-600">Adherencia:</span>
                        <span className="ml-1 font-medium">{patient.compliance}%</span>
                      </div>
                      
                      <div className="bg-blue-50 rounded-lg p-3 mt-3">
                        <p className="text-sm text-blue-800">
                          <strong>IA:</strong> {patient.aiRecommendation}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Insights Predictivos */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="bg-white rounded-xl shadow-sm border p-6"
            >
              <div className="flex items-center space-x-2 mb-6">
                <h3 className="text-lg font-semibold text-gray-900">Insights Predictivos</h3>
                <InfoTooltip 
                  title="Motor de Insights Predictivos Médicos"
                  description="Generación automática de insights y recomendaciones basadas en análisis predictivo para mejorar la atención médica."
                  aiMethod="Análisis predictivo de tendencias de salud, recomendaciones personalizadas, optimización de tratamientos."
                  dataSource="Big data médico, patrones de salud poblacional, efectividad de tratamientos, research médico."
                  updateFrequency="Insights generados diariamente con actualizaciones basadas en nueva evidencia"
                  iconColor="text-gray-400"
                />
              </div>
              
              <div className="space-y-3">
                {aiInsights.map((insight, index) => (
                  <div key={index} className="border border-gray-100 rounded-lg p-3">
                    <div className="flex items-start space-x-3">
                      <div className="p-2 bg-red-100 rounded-full flex-shrink-0">
                        <Brain className="w-4 h-4 text-red-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-gray-900">{insight.message}</p>
                        <div className="flex items-center justify-between mt-2">
                          <span className="text-xs text-gray-500">Confianza: {insight.confidence}%</span>
                          <button className="text-xs text-red-600 hover:text-red-800 font-medium">
                            {insight.action}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </main>
    </div>
  )
} 